// Dimensions that have entity lookups in dimensionInfo (id -> name)
export const ENTITY_DIMENSIONS = {
  campaign_id: { source: 'campaignId.name', label: 'Campaign' },
  flow_id: { source: 'flowId.name', label: 'Flow' },
  client_id: { source: 'clientId.name', label: 'Client' },
}

export const TIME_BUCKETS = ['hour', 'day', 'week', 'month', 'quarter', 'year']

function defaultTimeDimension(queryInfo) {
  const timeDim = queryInfo.availableDimensions?.find(d => d.kind === 'time')
  return timeDim ? { id: timeDim.id, timeBucket: 'day' } : null
}

function defaultCategoryDimension(queryInfo) {
  const dim = queryInfo.availableDimensions?.find(d => d.kind !== 'time')
  return dim ? { id: dim.id } : null
}

function defaultXDimension(queryInfo) {
  const timeDim = queryInfo.availableDimensions?.find(d => d.kind === 'time')
  if (timeDim) return { id: timeDim.id, timeBucket: 'day' }
  const firstDim = queryInfo.availableDimensions?.[0]
  return firstDim ? { id: firstDim.id } : null
}

// Each chart type defines:
//   label, description  — UI display
//   slots               — what the user configures
//   defaults(queryInfo) — initial slot values
//   supportsOrderLimit  — whether to show the order/limit section
//   collectDimensions(slotValues) — returns ordered array of dimension objects from slot values
//   buildChartOptions(slotValues, metricIds) — returns chart-specific options (x, y, seriesBy, etc.)

const barColumnBase = {
  supportsOrderLimit: true,
  slots: {
    metrics: { label: 'Metrics', required: true, kind: 'metric', single: false },
    xAxis: { label: 'X axis', required: true, kind: 'dimension', single: true, includeTime: true },
    segmentBy: { label: 'Segment by', required: false, kind: 'dimension', single: true },
    stacked: { label: 'Stacked', required: false, kind: 'boolean' },
  },
  defaults: (queryInfo) => ({
    xAxis: defaultXDimension(queryInfo),
    metrics: queryInfo.defaultMetrics?.map(m => m.id) || ['count'],
    segmentBy: null,
    stacked: false,
  }),
  collectDimensions: (slotValues) => {
    const dims = []
    if (slotValues.xAxis) dims.push(slotValues.xAxis)
    if (slotValues.segmentBy) dims.push(slotValues.segmentBy)
    return dims
  },
  buildChartOptions: (slotValues, metricIds) => {
    const opts = {}
    opts.x = _.camelCase(slotValues.xAxis?.id || 'time')
    opts.y = metricIds.length === 1
      ? _.camelCase(metricIds[0])
      : metricIds.map(id => _.camelCase(id))
    if (slotValues.segmentBy) opts.seriesBy = _.camelCase(slotValues.segmentBy.id)
    if (slotValues.stacked) opts.stacked = true
    return opts
  },
}

export const CHART_TYPES = {
  lineV2: {
    label: 'Line Chart',
    description: 'Track metrics over time',
    supportsOrderLimit: true,
    slots: {
      metrics: { label: 'Metrics', required: true, kind: 'metric', single: false },
      timeDimension: { label: 'Time dimension', required: true, kind: 'time', single: true },
      segmentBy: { label: 'Segment by', required: false, kind: 'dimension', single: true },
      comparePrevious: { label: 'Compare with previous period', required: false, kind: 'boolean' },
    },
    defaults: (queryInfo) => ({
      timeDimension: defaultTimeDimension(queryInfo),
      metrics: queryInfo.defaultMetrics?.map(m => m.id) || ['count'],
      segmentBy: null,
      comparePrevious: false,
    }),
    collectDimensions: (slotValues) => {
      const dims = []
      if (slotValues.timeDimension) dims.push(slotValues.timeDimension)
      if (slotValues.segmentBy) dims.push(slotValues.segmentBy)
      return dims
    },
    buildChartOptions: (slotValues, metricIds) => {
      const opts = {}
      opts.x = _.camelCase(slotValues.timeDimension?.id || 'time')
      opts.y = metricIds.length === 1
        ? _.camelCase(metricIds[0])
        : metricIds.map(id => _.camelCase(id))
      if (slotValues.segmentBy) {
        opts.seriesBy = _.camelCase(slotValues.segmentBy.id)
      }
      if (slotValues.comparePrevious) opts.comparePrevious = true
      return opts
    },
  },
  columnV2: {
    ...barColumnBase,
    label: 'Column Chart',
    description: 'Compare values across categories or time',
  },
  barV2: {
    ...barColumnBase,
    label: 'Bar Chart',
    description: 'Horizontal bars for category comparison',
    buildChartOptions: (slotValues, metricIds) => ({
      ...barColumnBase.buildChartOptions(slotValues, metricIds),
      orientation: 'h',
    }),
  },
  pieV2: {
    label: 'Pie Chart',
    description: 'Show proportions of a whole',
    supportsOrderLimit: true,
    slots: {
      metrics: { label: 'Metric', required: true, kind: 'metric', single: true },
      sliceBy: { label: 'Slice by', required: true, kind: 'dimension', single: true },
      donut: { label: 'Donut style', required: false, kind: 'boolean' },
    },
    defaults: (queryInfo) => ({
      sliceBy: defaultCategoryDimension(queryInfo),
      metrics: [queryInfo.defaultMetrics?.[0]?.id || 'count'],
      donut: false,
    }),
    collectDimensions: (slotValues) => {
      const dims = []
      if (slotValues.sliceBy) dims.push(slotValues.sliceBy)
      return dims
    },
    buildChartOptions: (slotValues, metricIds) => ({
      labels: _.camelCase(slotValues.sliceBy?.id || ''),
      values: _.camelCase(metricIds[0] || 'count'),
      donut: !!slotValues.donut,
    }),
  },
  table: {
    label: 'Table',
    description: 'Rows grouped by dimensions',
    supportsOrderLimit: true,
    slots: {
      metrics: { label: 'Metrics', required: true, kind: 'metric', single: false },
      groupBy: { label: 'Group by', required: false, kind: 'dimension', single: false },
    },
    defaults: (queryInfo) => ({
      metrics: queryInfo.defaultMetrics?.map(m => m.id) || ['count'],
      groupBy: [],
    }),
    collectDimensions: (slotValues) => {
      const dims = []
      if (Array.isArray(slotValues.groupBy)) {
        slotValues.groupBy.forEach(d => dims.push(d))
      }
      return dims
    },
    buildChartOptions: () => ({}),
  },
  number: {
    label: 'Number',
    description: 'Single metric value',
    supportsOrderLimit: false,
    slots: {
      metrics: { label: 'Metric', required: true, kind: 'metric', single: true },
      comparePrevious: { label: 'Compare with previous period', required: false, kind: 'boolean' },
    },
    defaults: (queryInfo) => ({
      metrics: [queryInfo.defaultMetrics?.[0]?.id || 'count'],
      comparePrevious: false,
    }),
    collectDimensions: () => [],
    buildChartOptions: (slotValues) => {
      const opts = {}
      if (slotValues.comparePrevious) opts.comparePrevious = true
      return opts
    },
  },
  gauge: {
    label: 'Gauge',
    description: 'Metric with range indicator',
    supportsOrderLimit: false,
    slots: {
      metrics: { label: 'Metric', required: true, kind: 'metric', single: true },
    },
    defaults: (queryInfo) => ({
      metrics: [queryInfo.defaultMetrics?.[0]?.id || 'count'],
    }),
    collectDimensions: () => [],
    buildChartOptions: () => ({}),
  },
  map: {
    label: 'Map',
    description: 'Choropleth map by geographic area',
    supportsOrderLimit: false,
    slots: {
      metrics: { label: 'Metric', required: true, kind: 'metric', single: true },
      geoDimension: { label: 'Geographic dimension', required: true, kind: 'geoDimension', single: true },
    },
    defaults: (queryInfo) => {
      const geoDim = queryInfo.availableDimensions?.find(d => d.geo)
      return {
        metrics: [queryInfo.defaultMetrics?.[0]?.id || 'count'],
        geoDimension: geoDim ? { id: geoDim.id } : null,
      }
    },
    collectDimensions: (slotValues) => {
      return slotValues.geoDimension ? [slotValues.geoDimension] : []
    },
    buildChartOptions: (slotValues, metricIds, queryInfo) => {
      const dimId = slotValues.geoDimension?.id
      const dimInfo = queryInfo?.availableDimensions?.find(d => d.id === dimId)
      return {
        mapId: dimInfo?.geo || 'world',
        values: _.camelCase(metricIds[0] || 'count'),
      }
    },
  },
  pivotTable: {
    label: 'Pivot Table',
    description: 'Heatmap grid with two dimensions',
    supportsOrderLimit: true,
    slots: {
      metrics:         { label: 'Metric', required: true, kind: 'metric', single: true },
      rowDimension:    { label: 'Row dimension', required: true, kind: 'dimension', single: true },
      columnDimension: { label: 'Column dimension', required: true, kind: 'dimension', single: true },
      timeDimension:   { label: 'Time dimension', required: false, kind: 'time', single: true },
      comparePrevious: { label: 'Compare with previous period', required: false, kind: 'boolean' },
    },
    defaults: (queryInfo) => ({
      metrics: [queryInfo.defaultMetrics?.[0]?.id || 'count'],
      rowDimension: defaultCategoryDimension(queryInfo),
      columnDimension: null,
      timeDimension: defaultTimeDimension(queryInfo),
      comparePrevious: false,
    }),
    collectDimensions: (slotValues) => {
      const dims = []
      if (slotValues.rowDimension) dims.push(slotValues.rowDimension)
      if (slotValues.columnDimension) dims.push(slotValues.columnDimension)
      if (slotValues.timeDimension) dims.push(slotValues.timeDimension)
      return dims
    },
    buildChartOptions: (slotValues, metricIds) => ({
      rowDimension: _.camelCase(slotValues.rowDimension?.id || ''),
      columnDimension: _.camelCase(slotValues.columnDimension?.id || ''),
      timeDimension: slotValues.timeDimension ? _.camelCase(slotValues.timeDimension.id) : null,
      metric: _.camelCase(metricIds[0] || 'count'),
      ...(slotValues.comparePrevious ? { comparePrevious: true } : {}),
    }),
  },
  htmlBlock: {
    label: 'HTML Block',
    description: 'Rich text content section',
    static: true,
    supportsOrderLimit: false,
    slots: {
      html: { label: 'Content', required: false, kind: 'html' },
    },
    defaults: () => ({
      html: '',
    }),
    collectDimensions: () => [],
    buildChartOptions: (slotValues) => ({
      html: slotValues.html || '',
    }),
  },
}
